import produce from "immer";
import { flatten, head, isEmpty, map, min, orderBy, reduce } from "lodash";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { DataById, PaymentTransaction } from "../../utils/types";
import { LoadingKey, useLoading } from "../loading/LoadingProvider";
import { useWorkspaces } from "../workspaces";

interface Props {}

export type ContextValue = {
  fetchPaymentTransactions: (walletAddresses: string[]) => Promise<void>;
  paymentTransactionsByWallet: DataById<PaymentTransaction[]>;
  paymentTransactionByHash: DataById<PaymentTransaction>;
};

export const PaymentTransactionsContext = React.createContext<
  ContextValue | undefined
>(undefined);

export const PaymentTransactionsProvider: React.FC<Props> = ({
  children,
  ...otherProps
}) => {
  const { currentWorkspaceId } = useWorkspaces();
  const { addLoader, removeLoader } = useLoading();
  const [paymentTransactionsByWallet, setPaymentTransactionsByWallet] =
    useState<DataById<PaymentTransaction[]>>({});
  const [paymentTransactionByHash, setPaymentTransactionByHash] = useState<
    DataById<PaymentTransaction>
  >({});

  const fetchPaymentTransactions = useCallback(
    async (walletAddresses: string[]) => {
      if (!currentWorkspaceId || isEmpty(walletAddresses)) return;

      addLoader(LoadingKey.paymentTransactions);

      const minimumBlockHeights = reduce(
        walletAddresses,
        (acc: number[], walletAddress) => {
          const walletTransactions = orderBy(
            paymentTransactionsByWallet[walletAddress] || [],
            "block",
            "desc"
          );

          return [
            ...acc,
            isEmpty(walletTransactions)
              ? MINIMUM_BLOCK_HEIGHT
              : (head(walletTransactions) as PaymentTransaction).block
          ];
        },
        []
      );

      try {
        const res = await fetch(
          `${process.env.REACT_APP_ETL_API}/wallets/payments/`,
          {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
              wallet_ids: walletAddresses,
              block: min([MINIMUM_BLOCK_HEIGHT, ...minimumBlockHeights])
            })
          }
        );

        const response: PaymentsResponse = await res.json();

        if (!isEmpty(response.wallets)) {
          setPaymentTransactionsByWallet(
            response.wallets.reduce(
              (acc: DataById<PaymentTransaction[]>, { wallet, data }) => {
                acc[wallet] = data || [];
                return acc;
              },
              {}
            )
          );

          setPaymentTransactionByHash((prevMap) =>
            produce(prevMap, (draft) => {
              flatten(
                map(response.wallets, "data").filter((item) => item)
              ).forEach((transaction) => {
                draft[transaction.hash] = transaction;
              });
            })
          );
        } else {
          setPaymentTransactionsByWallet({});
        }
      } catch (e) {
        console.error(e);
      }

      removeLoader(LoadingKey.paymentTransactions);
    },
    [addLoader, currentWorkspaceId, removeLoader, paymentTransactionsByWallet]
  );

  const value = useMemo(
    () => ({
      fetchPaymentTransactions,
      paymentTransactionsByWallet,
      paymentTransactionByHash
    }),
    [
      fetchPaymentTransactions,
      paymentTransactionsByWallet,
      paymentTransactionByHash
    ]
  );

  return (
    <PaymentTransactionsContext.Provider value={value} {...otherProps}>
      {children}
    </PaymentTransactionsContext.Provider>
  );
};

export const usePaymentTransactions = (): ContextValue => {
  const context = useContext(PaymentTransactionsContext);

  if (context === undefined) {
    throw new Error(
      "usePaymentTransactions must be used within an PaymentTransactionsProvider"
    );
  }

  return context;
};

//
// Utils
//

interface PaymentsResponse {
  object: "list";
  url: "/wallets/payments/";
  from_block: 0;
  wallets: {
    wallet: string;
    including_pending_transactions: boolean;
    data: PaymentTransaction[];
  }[];
}

const MINIMUM_BLOCK_HEIGHT = 1034000;
